<script setup lang="ts">
const props = defineProps<{
  id: string
  colors?: string[]
  gradientOptions?: {
    speed?: number
    amplitude?: number
    density?: [number, number]
  }
}>()

const defaultColors = ['#C2FFD3', '#41C0B3', '#FFFFFF', '#ABEF96', '#FFFFFF', '#FFE500']

const canvasRef = useTemplateRef<HTMLCanvasElement>('canvasRef')

let rafId = 0
let observer: ResizeObserver | null = null
let gl: WebGLRenderingContext | null = null
let program: WebGLProgram | null = null
let startTime = 0

const VERT_SRC = /* glsl */ `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

// 4-color organic blob gradient using layered noise — Stripe-style
const FRAG_SRC = /* glsl */ `
  precision mediump float;

  uniform float u_time;
  uniform vec3 u_colors[4];
  uniform float u_amplitude;
  uniform vec2 u_density;

  varying vec2 v_uv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  // Value noise, returns [-1, 1]
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash(i + vec2(0.0, 0.0));
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y) * 2.0 - 1.0;
  }

  // Each color is placed at a distinct 2D position that drifts over time.
  // For each fragment, compute a weighted blend based on distance to each blob.
  void main() {
    vec2 uv = v_uv; // [0, 1]

    float t = u_time * 0.0002;
    // Lower scale = wider, softer blobs; each color covers more area
    float scale = mix(0.6, 0.3, clamp(u_density.x * 2.0, 0.0, 1.0));

    // Add a small noise warp so edges aren't perfectly circular
    float warpAmt = u_amplitude / 400.0;
    vec2 warp = vec2(
      noise(uv * 2.3 + vec2(t * 0.4, t * 0.3)),
      noise(uv * 2.3 + vec2(t * 0.3 + 5.2, t * 0.5 + 1.7))
    ) * warpAmt;
    vec2 wp = uv + warp;

    // 5 blobs: colors 0–2 get one blob each, lime (color 3) gets two
    vec2 b0 = vec2(0.5 + 0.55 * sin(t * 1.10 + 0.0), 0.5 + 0.50 * cos(t * 0.90 + 0.0));
    vec2 b1 = vec2(0.5 + 0.55 * cos(t * 0.80 + 2.1), 0.5 + 0.50 * sin(t * 1.20 + 2.1));
    vec2 b2 = vec2(0.5 + 0.55 * sin(t * 1.30 + 4.2), 0.5 + 0.50 * cos(t * 0.70 + 4.2));
    // lime blob A — opposite phase to b2 so they spread apart
    vec2 b3 = vec2(0.5 + 0.55 * cos(t * 0.95 + 1.1), 0.5 + 0.50 * sin(t * 1.05 + 3.3));
    // lime blob B — slower, different axis, stays on opposite side of canvas
    vec2 b4 = vec2(0.5 + 0.50 * sin(t * 0.65 + 5.8), 0.5 + 0.55 * cos(t * 1.15 + 0.7));

    float w0 = 1.0 / (dot(wp - b0, wp - b0) * scale + 0.001);
    float w1 = 1.0 / (dot(wp - b1, wp - b1) * scale + 0.001);
    float w2 = 1.0 / (dot(wp - b2, wp - b2) * scale + 0.001);
    float w3 = 1.0 / (dot(wp - b3, wp - b3) * scale + 0.001);
    float w4 = 1.0 / (dot(wp - b4, wp - b4) * scale + 0.001);

    float wsum = w0 + w1 + w2 + w3 + w4;
    vec3 color = (u_colors[0] * w0 + u_colors[1] * w1 + u_colors[2] * w2 + u_colors[3] * (w3 + w4)) / wsum;

    gl_FragColor = vec4(color, 1.0);
  }
`

function hexToVec3(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  const r = Number.parseInt(h.substring(0, 2), 16) / 255
  const g = Number.parseInt(h.substring(2, 4), 16) / 255
  const b = Number.parseInt(h.substring(4, 6), 16) / 255
  return [r, g, b]
}

function compileShader(glCtx: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const shader = glCtx.createShader(type)
  if (!shader) return null
  glCtx.shaderSource(shader, src)
  glCtx.compileShader(shader)
  return shader
}

function initWebGL(canvas: HTMLCanvasElement) {
  const ctx = (canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
  if (!ctx) return
  gl = ctx

  const vert = compileShader(gl, gl.VERTEX_SHADER, VERT_SRC)
  const frag = compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC)
  if (!vert || !frag) return

  const prog = gl.createProgram()
  if (!prog) return
  program = prog
  gl.attachShader(program, vert)
  gl.attachShader(program, frag)
  gl.linkProgram(program)
  gl.useProgram(program)

  // Fullscreen quad: 2 triangles
  const positions = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1])
  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW)

  const posLoc = gl.getAttribLocation(program, 'a_position')
  gl.enableVertexAttribArray(posLoc)
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)

  // Upload initial color uniforms
  uploadColors()
}

function uploadColors() {
  if (!gl || !program) return
  const glCtx = gl
  const prog = program
  const palette = (props.colors ?? defaultColors).slice(0, 4)
  palette.forEach((hex, i) => {
    const loc = glCtx.getUniformLocation(prog, `u_colors[${i}]`)
    glCtx.uniform3fv(loc, hexToVec3(hex))
  })
}

function startAnimation() {
  startTime = performance.now()

  const speed = props.gradientOptions?.speed ?? 1.0
  // amplitude: controls warp intensity. Default 80 = visible but smooth.
  // density:   maps to blob spread scale. Default [0.5, 0.5] = even coverage.
  const amplitude = props.gradientOptions?.amplitude ?? 80
  const density = props.gradientOptions?.density ?? [0.5, 0.5]

  function frame(now: number) {
    if (!gl || !program) return
    const elapsed = (now - startTime) * speed

    const timeLoc = gl.getUniformLocation(program, 'u_time')
    gl.uniform1f(timeLoc, elapsed)

    const ampLoc = gl.getUniformLocation(program, 'u_amplitude')
    gl.uniform1f(ampLoc, amplitude)

    const densityLoc = gl.getUniformLocation(program, 'u_density')
    gl.uniform2f(densityLoc, density[0], density[1])

    gl.drawArrays(gl.TRIANGLES, 0, 6)
    rafId = requestAnimationFrame(frame)
  }

  rafId = requestAnimationFrame(frame)
}

function handleResize(canvas: HTMLCanvasElement) {
  observer = new ResizeObserver(entries => {
    if (!gl || !entries?.[0]) return
    const entry = entries[0]
    const {width, height} = entry.contentRect
    const dpr = window.devicePixelRatio ?? 1
    canvas.width = width * dpr
    canvas.height = height * dpr
    gl.viewport(0, 0, canvas.width, canvas.height)
  })
  observer.observe(canvas)
  // Trigger initial resize using contentRect approach via a manual call
  const dpr = window.devicePixelRatio ?? 1
  const rect = canvas.getBoundingClientRect()
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
}

function cleanup(canvas: HTMLCanvasElement) {
  cancelAnimationFrame(rafId)
  observer?.disconnect()
  const ext = canvas.getContext('webgl')?.getExtension('WEBGL_lose_context')
  ext?.loseContext()
  gl = null
  program = null
}

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return
  try {
    handleResize(canvas)
    initWebGL(canvas)
    startAnimation()
  } catch {
    // Silently fail if WebGL is unavailable
  }
})

onUnmounted(() => {
  const canvas = canvasRef.value
  if (canvas) cleanup(canvas)
})
</script>

<template>
  <canvas ref="canvasRef" :id="id" class="block h-full w-full" />
</template>
